import React, { useContext, useEffect, useRef, useState } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import Text from 'components/Text/Text';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import { createAppError } from 'utils/helpers';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import style from './FileUploader.module.scss';

export type TFileUploaderFileType = 'zip' | 'bin' | 'apk' | 'html' | 'mp4' | 'svg' | 'json' | 'png' | 'jpg';

interface IFileUploader {
	fileType: TFileUploaderFileType | TFileUploaderFileType[];
	id: string;
	label: string;
	onFileLoad: (file: File, callback: (value: string) => void) => void;
	filePath?: string | null;
	onFileReset?: () => void;
	requiredValidationMessage?: string;
	width?: number;
	messagePosition?: 'right' | 'bottom';
	errorMessage?: string;
	defaultFileName?: string | null;
}

const validFileExtensionsMap: { [key in TFileUploaderFileType]: string[] } = {
	zip: ['zip', 'application/zip', 'application/x-zip', 'application/x-zip-compressed'],
	bin: ['application/octet-stream', 'application/macbinary', 'application/x-macbinary'],
	apk: ['apk', 'application/vnd.android.package-archive'],
	html: ['text/html'],
	mp4: ['video/mp4'],
	svg: ['image/svg+xml'],
	json: ['application/json'],
	png: ['image/png'],
	jpg: ['image/jpeg'],
};

const FileUploader: React.FC<IFileUploader> = ({
	fileType,
	onFileLoad,
	onFileReset,
	id,
	label,
	filePath,
	requiredValidationMessage,
	width = 350,
	messagePosition = 'right',
	errorMessage = 'errorAddingFile',
	defaultFileName,
}) => {
	const { addAppError } = useContext<IAppContext>(AppContext);
	const [fileName, setFileName] = useState<string | null>(null);
	const containerClasses = classNames(style.container, style[messagePosition]);
	const initialFileType = useRef(fileType);
	const { t } = useTranslation('error');

	useEffect(() => {
		if (filePath === null) {
			setFileName(null);
		}
	}, [filePath]);

	useEffect(() => {
		const isFileTypeChanged = initialFileType.current.toString() !== fileType.toString();
		if (isFileTypeChanged) {
			onFileReset?.();
			setFileName(null);
		}
	}, [fileType, onFileReset, initialFileType]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.preventDefault();
		const file = e.target.files ? e.target.files[0] : '';

		if (!file) {
			return;
		}

		let isValidFile = true;
		const fileName = file.name;
		const fileExtensionStartIndex = fileName.lastIndexOf('.') + 1;
		const fileExtension = fileName.slice(fileExtensionStartIndex);

		if (Array.isArray(fileType)) {
			isValidFile = fileType.some(
				(type) => validFileExtensionsMap[type].includes(file.type) || fileExtension === type,
			);
		} else {
			isValidFile = validFileExtensionsMap[fileType].includes(file.type) || fileExtension === fileType;
		}

		if (isValidFile) {
			onFileLoad(file, setFileName);
		} else {
			const fileFormat = Array.isArray(fileType) ? fileType.map((type) => `.${type}`).join(', ') : `.${fileType}`;

			const appError = createAppError({
				message: t(errorMessage, { format: fileFormat }),
				name: '',
				stack: '',
			});
			addAppError(appError);
		}
		e.target.value = '';
	};

	const showMessage = () => {
		if (requiredValidationMessage) {
			return (
				<Text
					cssStyle={{ maxWidth: `${width}px` }}
					className={style.requiredValidationError}
					translationNameSpace="error"
					translationKey={requiredValidationMessage}
					theme="error"
				/>
			);
		}
		return (
			<div style={{ maxWidth: `${width}px` }} className={style.fileName}>
				{!fileName && defaultFileName ? defaultFileName : fileName}
			</div>
		);
	};

	const acceptedFiles = Array.isArray(fileType) ? fileType.map((el) => `.${el}`).join(', ') : `.${fileType}`;

	return (
		<div className={containerClasses} data-test="FileUploader">
			<input className={style.input} onChange={handleChange} id={id} type="file" accept={acceptedFiles} />
			<label htmlFor={id}>
				<div style={{ width: `${width}px` }} className={style.loader}>
					<div className={style.label}>
						<svg className={style.icon}>
							<use xlinkHref={`${breadcrumbs}#uploadArrow`} />
						</svg>
						<Text translationNameSpace="form" translationKey={label} asFragment={true} />
					</div>
				</div>
			</label>
			{showMessage()}
		</div>
	);
};

export default React.memo(FileUploader);
